export const LEGAL_DOCS = {
  privacy: {
    slug: "privacy-policy",
    title: "Privacy Policy",
    seoTitle: "Privacy Policy | OneCare Pharmacy",
    description: "How OneCare Pharmacy collects, uses and protects your personal information when you use onecarelife.health.",
    intro:
      "This Privacy Policy explains what information OneCare Pharmacy (\"we\", \"us\", \"our\") collects when you use onecarelife.health, how we use it, and the choices you have. By using our website or contacting us, you agree to this policy.",
    sections: [
      {
        h: "Who we are",
        paras: [
          "OneCare Pharmacy operates the website onecarelife.health, an online pharmacy catalogue and enquiry service. You can reach us at info@onecarelife.health, by phone on +1 (775) 416-9317, or on WhatsApp at +91 85458 84773.",
        ],
      },
      {
        h: "Information we collect",
        paras: [
          "Enquiry details you give us: your name, email address, country or region, enquiry type, message, and the products you include in your enquiry basket.",
          "Correspondence: records of emails, WhatsApp messages and calls so we can follow up on your questions and orders.",
          "Technical information: device, browser and usage data collected through standard website analytics to help us keep the site fast and reliable.",
        ],
      },
      {
        h: "How we use your information",
        paras: [
          "To respond to your enquiries, confirm product availability and supply eligibility for your region, process and deliver confirmed orders, and provide customer support.",
          "To keep transaction records required for accounting, legal and regulatory purposes, and to improve our website and service.",
          "We do not sell your personal information to anyone.",
        ],
      },
      {
        h: "Please don't send sensitive medical details",
        paras: [
          "Our forms only need your contact details and your question. Please do not send unnecessary sensitive medical information by email or chat. Where a lawful supply process requires specific documentation, our team will tell you exactly what is needed and how to share it safely.",
        ],
      },
      {
        h: "How we share information",
        paras: [
          "We share information only with the service providers needed to run our business — for example website hosting, email delivery, and payment processing (Razorpay) where you choose to pay online. These providers may only use your data to perform their service for us.",
          "We may disclose information where required by law, regulation, or a valid legal request, or to protect the safety and rights of our customers and our business.",
        ],
      },
      {
        h: "Cookies and analytics",
        paras: [
          "We use cookies and analytics tools to understand how visitors use the site and to keep your enquiry basket saved on your device. You can control or delete cookies through your browser settings; the site will still work, though your basket may not be remembered.",
        ],
      },
      {
        h: "Data retention and security",
        paras: [
          "We keep enquiry and order records for as long as needed to provide support and meet legal or accounting obligations, after which they are deleted or anonymised.",
          "We use reasonable technical and organisational measures to protect your information. No online system is completely secure, so please contact us immediately if you believe your information has been compromised.",
        ],
      },
      {
        h: "Your rights and choices",
        paras: [
          "You can ask us at any time to access, correct or delete the personal information we hold about you, or to stop non-essential communications. Email info@onecarelife.health and we will respond as soon as possible.",
        ],
      },
      {
        h: "Changes to this policy",
        paras: [
          "We may update this Privacy Policy from time to time. The latest version is always published on this page with its revision date.",
        ],
      },
    ],
  },
  terms: {
    slug: "terms-and-conditions",
    title: "Terms and Conditions",
    seoTitle: "Terms and Conditions | OneCare Pharmacy",
    description: "The terms that govern your use of the OneCare Pharmacy website, catalogue, enquiry service and orders.",
    intro:
      "These Terms and Conditions govern your use of onecarelife.health and any enquiries or orders you place with OneCare Pharmacy. Please read them carefully before using the site.",
    sections: [
      {
        h: "Agreement to these terms",
        paras: [
          "By browsing this website, submitting an enquiry, or placing an order, you confirm that you are at least 18 years old and that you accept these Terms together with our Privacy Policy. If you do not agree, please do not use the site.",
        ],
      },
      {
        h: "Our service",
        paras: [
          "OneCare Pharmacy is an online pharmacy catalogue and enquiry service. The website displays product information, pack sizes and prices. Submitting the enquiry or checkout form is a request to purchase, not a completed sale — every order is reviewed and confirmed by our team by email before supply.",
          "We may decline or cancel any enquiry or order, in whole or in part, where supply would be unlawful, inappropriate or unavailable in your location.",
        ],
      },
      {
        h: "Eligibility and prescription requirements",
        paras: [
          "Some products require a valid prescription or other clinical authorisation. Such products are supplied only through a lawful, appropriately authorised process, and we may ask you to provide supporting information before confirming an order.",
          "It is your responsibility to ensure that purchasing and importing a product is permitted in your country or region. Product availability and supply eligibility vary by location and applicable requirements.",
        ],
      },
      {
        h: "Product information and images",
        paras: [
          "We work to keep catalogue details — item names, strengths, pack sizes and displayed prices — accurate, and we review them before publication. Product photographs are representative of the item or its packaging and may differ from the pack you receive.",
          "Content on this website is for general information only and may be updated, corrected or withdrawn at any time without notice.",
        ],
      },
      {
        h: "Prices and currencies",
        paras: [
          "Catalogue prices are displayed in US dollars. A currency toggle can show approximate prices in British pounds using a fixed conversion rate; converted figures are indicative only.",
          "The final price, currency and any applicable shipping charges are confirmed in writing by our team before any payment is taken or order dispatched.",
        ],
      },
      {
        h: "Payments",
        paras: [
          "Where online payment is offered, payments are processed securely by Razorpay. We never see or store your full card or banking details on our servers.",
          "A payment authorises us to fulfil your order but does not replace order confirmation. If we cannot accept your order after payment, we refund it in full to the original payment method.",
        ],
      },
      {
        h: "Order confirmation and cancellation",
        paras: [
          "Your order is accepted only when our team confirms it by email. You may cancel an enquiry at any time before confirmation at no cost. For paid orders, cancellations before dispatch are refunded in full; after dispatch, our Return & Refund Policy applies.",
        ],
      },
      {
        h: "Acceptable use",
        paras: [
          "You agree to use this website lawfully and honestly: provide accurate contact and order information, do not attempt to interfere with the site's security or operation, and do not use the site for resale, fraud or any unlawful purpose.",
        ],
      },
      {
        h: "No medical advice",
        paras: [
          "Nothing on this website is medical advice. Always consult a qualified healthcare professional before starting, stopping or changing any medication. Product information on the site does not replace the patient information leaflet supplied with the product.",
        ],
      },
      {
        h: "Liability and governing law",
        paras: [
          "To the maximum extent permitted by law, OneCare Pharmacy is not liable for indirect or consequential losses arising from use of this website or reliance on its content. Nothing in these terms excludes liability that cannot be excluded by law.",
          "These Terms are governed by the laws of India, and any dispute is subject to the exclusive jurisdiction of the courts of the operator's home jurisdiction, without affecting any consumer rights you may have locally.",
        ],
      },
    ],
  },
  returns: {
    slug: "return-refund-policy",
    title: "Return & Refund Policy",
    seoTitle: "Return & Refund Policy | OneCare Pharmacy",
    description: "How OneCare Pharmacy handles returns, replacements and refunds for damaged, incorrect or undelivered orders.",
    intro:
      "Because we supply healthcare products, returns are handled differently from ordinary retail. This policy explains when returns, replacements and refunds are possible and how to request them.",
    sections: [
      {
        h: "Our approach",
        paras: [
          "For safety and regulatory reasons, medicines generally cannot be restocked once they leave our control. We therefore replace or refund orders that arrive damaged, incorrect or incomplete rather than asking you to post products back, unless we specifically ask for a return.",
        ],
      },
      {
        h: "Report issues within 48 hours",
        paras: [
          "Please check your parcel on arrival. If an item is damaged, incorrect or missing, email info@onecarelife.health within 48 hours of delivery with your order reference and clear photographs of the product and packaging. Reports made after this window are reviewed case by case.",
        ],
      },
      {
        h: "Items we cannot take back",
        paras: [
          "We cannot accept returns or offer refunds for products that have been opened or used, products returned without our authorisation, or orders refused because of import restrictions in the destination country (see our Shipping Policy).",
        ],
      },
      {
        h: "Approved returns",
        paras: [
          "Once we approve your claim, we will offer a replacement at no extra cost or a refund — whichever you prefer and stock allows. Where a return is required, we will email you the return instructions; please do not send products back before receiving them.",
        ],
      },
      {
        h: "Refunds: method and timing",
        paras: [
          "Refunds are issued to your original payment method. Card and UPI payments made through Razorpay are refunded through Razorpay and typically reach your account within 5–7 business days after approval, depending on your bank.",
        ],
      },
      {
        h: "Cancellations",
        paras: [
          "You can cancel an enquiry at any time before our team confirms your order — no payment is taken for unconfirmed enquiries. Paid orders cancelled before dispatch are refunded in full. Once an order has been dispatched it can no longer be cancelled, and the returns process above applies instead.",
        ],
      },
      {
        h: "Failed deliveries and returned parcels",
        paras: [
          "If a parcel is returned to us because the address was incorrect or the delivery was refused, we can reship it (a reshipping fee may apply) or refund the product price minus shipping and handling costs. Parcels seized by customs are governed by our Shipping Policy.",
        ],
      },
    ],
  },
  shipping: {
    slug: "shipping-policy",
    title: "Shipping Policy",
    seoTitle: "Shipping Policy | OneCare Pharmacy",
    description: "Worldwide delivery information for OneCare Pharmacy orders: processing times, estimates, tracking, customs and packaging.",
    intro:
      "OneCare Pharmacy ships worldwide. This policy explains how orders are processed, how long delivery typically takes, and what to expect with international shipments.",
    sections: [
      {
        h: "Where we deliver",
        paras: [
          "We ship to most countries worldwide. Product availability, delivery options and supply eligibility vary by destination and the requirements that apply in each market — our team confirms what is possible for your location by email before any order is finalised.",
        ],
      },
      {
        h: "Processing time",
        paras: [
          "Orders are prepared after our team confirms your order by email (and after payment, where applicable). Parcels are typically dispatched within 24–72 hours of confirmation, excluding weekends and public holidays.",
        ],
      },
      {
        h: "Delivery estimates",
        paras: [
          "Standard international delivery typically takes 7–21 business days depending on your region and local postal services. These are estimates, not guarantees — customs inspections and local carrier delays can extend transit times.",
        ],
      },
      {
        h: "Discreet packaging",
        paras: [
          "All orders are packed securely in plain, discreet packaging that does not describe the contents or our pharmacy name on the outside.",
        ],
      },
      {
        h: "Tracking",
        paras: [
          "Where the destination service supports it, tracking details are emailed to you when your parcel is dispatched. If your tracking has not updated for an extended period, contact us and we will investigate with the carrier.",
        ],
      },
      {
        h: "Customs and import duties",
        paras: [
          "International orders may be subject to customs inspection, import duties or taxes in the destination country. These charges, and responsibility for complying with local import rules, rest with the recipient. Please check your local regulations before ordering, as parcels refused or seized by customs cannot always be refunded.",
        ],
      },
      {
        h: "Address accuracy",
        paras: [
          "Please double-check your delivery address in your enquiry or confirmation email. We ship to the address confirmed in writing, and cannot refund orders delivered to an incorrectly supplied address. Corrections are possible until the parcel is dispatched.",
        ],
      },
      {
        h: "Delays and lost parcels",
        paras: [
          "If your order has not arrived within 30 days of dispatch, contact info@onecarelife.health with your order reference. After a carrier investigation, we will reship your order or refund it in line with our Return & Refund Policy.",
        ],
      },
    ],
  },
};
